import React, { useState } from "react";
import { GripVertical, Edit, Trash2, Eye, EyeOff, Copy } from "lucide-react";
import { Button } from "../../../Components/ui/shadcn/button";
import { cn } from "../../../lib/utils";

interface Facility {
    id: number;
    name: string;
    image?: string;
    is_active: boolean;
    display_order: number;
}

interface DraggableFacilityListProps {
    facilities: Facility[];
    onReorder: (newOrder: Facility[]) => void;
    onEdit: (facility: Facility) => void;
    onDelete: (id: number) => void;
    onToggleActive: (id: number) => void;
    onCopy: (facility: Facility) => void;
}

const DraggableFacilityList: React.FC<DraggableFacilityListProps> = ({
    facilities,
    onReorder,
    onEdit,
    onDelete,
    onToggleActive,
    onCopy,
}) => {
    const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
        // For transparent drag image if needed
        const target = e.target as HTMLElement;
        target.classList.add("opacity-50");
    };

    const handleDragEnd = (e: React.DragEvent) => {
        const target = e.target as HTMLElement;
        target.classList.remove("opacity-50");
        setDraggedItemIndex(null);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === index) return;

        const newFacilities = [...facilities];
        const draggedItem = newFacilities[draggedItemIndex];
        newFacilities.splice(draggedItemIndex, 1);
        newFacilities.splice(index, 0, draggedItem);

        // Update display_order based on new index
        const updatedFacilities = newFacilities.map((item, i) => ({
            ...item,
            display_order: i,
        }));

        setDraggedItemIndex(index);
        onReorder(updatedFacilities);
    };

    return (
        <div className="space-y-2">
            {facilities.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 text-sm">
                    등록된 시설이 없습니다. "+ 추가" 버튼을 눌러 등록해주세요.
                </div>
            ) : (
                facilities.map((facility, index) => (
                    <div
                        key={facility.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        className={cn(
                            "flex items-center gap-4 bg-white p-3 rounded-lg border border-gray-200 shadow-sm transition-all hover:border-gray-300",
                            draggedItemIndex === index && "ring-2 ring-black bg-gray-50"
                        )}
                    >
                        <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                            <GripVertical size={20} />
                        </div>

                        <div className="w-12 h-12 bg-gray-100 rounded-md overflow-hidden shrink-0 border border-gray-100 text-[10px] text-gray-300 flex items-center justify-center">
                            {facility.image ? (
                                <img src={facility.image} alt={facility.name} className="w-full h-full object-cover" />
                            ) : (
                                "No Img"
                            )}
                        </div>

                        <div className="flex-grow">
                            <div className="text-sm font-semibold text-gray-900">{facility.name}</div>
                            <div className="text-[10px] text-gray-500">순서: {facility.display_order + 1}</div>
                        </div>

                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-gray-600"
                                onClick={() => onToggleActive(facility.id)}
                            >
                                {facility.is_active ? <Eye size={18} /> : <EyeOff size={18} className="text-red-400" />}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-blue-600"
                                onClick={() => onEdit(facility)}
                            >
                                <Edit size={18} />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-green-600"
                                onClick={() => onCopy(facility)}
                            >
                                <Copy size={18} />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-red-600"
                                onClick={() => onDelete(facility.id)}
                            >
                                <Trash2 size={18} />
                            </Button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default DraggableFacilityList;
